import {z} from 'zod';

/**
 * One month of the issue history. The API returns every closed month of the window, including the
 * ones without any movement (zero quantity), in ascending order.
 */
export const AbcXyzAnalysisDetailMonthSchema = z.object({
  month: z.string().nullish(),
  issuedQuantity: z.number().nullish(),
  returnedQuantity: z.number().nullish(),
  analysisQuantity: z.number().nullish(),
  isOutlier: z.boolean().nullish(),
});

/**
 * One month of the value history behind the ABC class. The API returns the twelve months of the
 * evaluation window, zero months included. `cumulativeSharePercent` is left out when the total of
 * the window is zero.
 */
export const AbcXyzAnalysisDetailValueMonthSchema = z.object({
  month: z.string().nullish(),
  netConsumptionValue: z.number().nullish(),
  cumulativeValue: z.number().nullish(),
  cumulativeSharePercent: z.number().nullish(),
});

/**
 * One row of the ABC/XYZ analysis report, addressed by the row's own id — the id the datagrid
 * reports for the clicked row.
 *
 * Every field is nullish: an article without an ABC item, an XYZ item or enough history still
 * gets a complete response with empty metrics and an explanatory status. The classes and metrics
 * are final — the frontend displays them and never recalculates them.
 *
 * `metadaBaseQuery` validates the response against this schema in dev, so a field the backend
 * renames or drops is reported instead of quietly rendering as a placeholder.
 */
export const AbcXyzAnalysisDetailResponseSchema = z.object({
  id: z.string().nullish(),
  abcSnapshotId: z.string().nullish(),
  xyzSnapshotId: z.string().nullish(),
  branchId: z.string().nullish(),
  branchName: z.string().nullish(),
  warehouseId: z.string().nullish(),
  warehouseName: z.string().nullish(),
  articleId: z.string().nullish(),
  articleNumber: z.string().nullish(),
  articleName: z.string().nullish(),
  unitName: z.string().nullish(),
  abcClass: z.string().nullish(),
  xyzClass: z.string().nullish(),
  abcXyzClass: z.string().nullish(),
  abcNetConsumptionValue: z.number().nullish(),
  abcSharePercent: z.number().nullish(),
  abcCumulativeSharePercent: z.number().nullish(),
  abcRank: z.number().nullish(),
  abcPeriodStart: z.string().nullish(),
  abcPeriodEnd: z.string().nullish(),
  abcHistoryMonthCount: z.number().nullish(),
  abcSnapshotDate: z.string().nullish(),
  abcRevision: z.number().nullish(),
  abcRulesVersion: z.string().nullish(),
  thresholdA: z.number().nullish(),
  thresholdB: z.number().nullish(),
  totalWarehouseValue: z.number().nullish(),
  isRapidGrowthA: z.boolean().nullish(),
  abcHasException: z.boolean().nullish(),
  abcQualityReason: z.string().nullish(),
  meanMonthlyQuantity: z.number().nullish(),
  coefficientOfVariation: z.number().nullish(),
  activeMonthCount: z.number().nullish(),
  seasonalityCorrelation: z.number().nullish(),
  seasonalityDetected: z.boolean().nullish(),
  xyzPeriodStart: z.string().nullish(),
  xyzPeriodEnd: z.string().nullish(),
  xyzHistoryMonthCount: z.number().nullish(),
  xyzSnapshotDate: z.string().nullish(),
  xyzRevision: z.number().nullish(),
  xyzRulesVersion: z.string().nullish(),
  thresholdCvX: z.number().nullish(),
  thresholdRY: z.number().nullish(),
  xyzResultStatus: z.string().nullish(),
  xyzHasException: z.boolean().nullish(),
  xyzExceptionCount: z.number().nullish(),
  xyzExceptionCodes: z.string().nullish(),
  insufficientHistory: z.boolean().nullish(),
  unestablishedReason: z.string().nullish(),
  monthlyHistory: z.array(AbcXyzAnalysisDetailMonthSchema).nullish(),
  /** Absent until the backend returns it, and for an article without an ABC item. */
  abcValueHistory: z.array(AbcXyzAnalysisDetailValueMonthSchema).nullish(),
  /** Whether `abcValueHistory` adds up to the snapshot's value. Absent without an ABC item. */
  abcValueHistoryMatchesSnapshot: z.boolean().nullish(),
});

export type AbcXyzAnalysisDetailResponse = z.infer<typeof AbcXyzAnalysisDetailResponseSchema>;
export type AbcXyzAnalysisDetailMonth = z.infer<typeof AbcXyzAnalysisDetailMonthSchema>;
export type AbcXyzAnalysisDetailValueMonth = z.infer<typeof AbcXyzAnalysisDetailValueMonthSchema>;
